import {Component, Input, OnInit, ElementRef, HostListener, OnDestroy, Renderer2} from '@angular/core';
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { TrialExplainerModalComponent } from "../trial-explainer/trial-explainer-modal.component";
import { ChangePlanModalComponent } from "../change-plan/change-plan-modal.component";
import { OrganisationService } from "../../services/organisation/organisation.service";
import { StripeService } from "../../services/stripe/stripe.service";
import * as Cookies from "js-cookie";
import { environment } from "../../../environments/environment";
import { CHARGEBEE_PLANS, COUNTRY_MAPPING, CountryOption, MAP_ORG_SIZE, PLAN_MAPPING, ORG_SIZE_MAPPING } from "./billing.types";
import { AlertBannerService, AlertType } from "../../lib/alert-banner";
import { Router } from "@angular/router";
import { PermissionsService } from "../../services/permissions/permissions.service";
import { ConstantsService } from "../../services/constants/constants.service";
import { ProfileService } from "../../services/profile/profile.service";

@Component({
  selector: 'app-billing-price',
  templateUrl: './billing.component.pug',
  styleUrls: ['./billing.component.scss'],
  standalone: false
})
export class BillingComponent implements OnInit, OnDestroy {
  @Input() preSelectedPlan = $localize`Growth`;
  @Input() preSelectedPlanDescription = $localize`Ideal for organisations with up to 150 employees`;

  isLoading = true;

  cardElement: any;
  expiryElement: any;
  cvvElement: any;
  stripeErrors = {
    cardNumber: '',
    expiry: '',
    cvv: ''
  };

  textValueFirstName = '';
  textValueLastName = '';
  address = '';
  city = '';
  state = '';
  country = '';
  zip = '';
  expiry = '';
  zipError = false;

  monthlyChecked = false;
  yearlyChecked = true;
  responsiveScreen = 0;
  selectedCurrency = '';

  trialExplainerModal: BsModalRef = new BsModalRef();
  changePlanModal: BsModalRef = new BsModalRef();
  plans: any[] = [];
  currentPricing = {
    monthly: 160,
    yearly: 140
  };

  dropDownLabel = $localize`Country`;
  dropDownOptions: Array<{ label: string; value: string }> = [];
  selectedCountry: CountryOption | undefined = undefined;

  isProcessing = false;
  paymentError = '';
  planDescription = '';
  selectedStripeCode = ''
  selectedEntity = ''
  showCurrencyDropdown = false;
  currencies = ['USD', 'AUD'];
  planTooltipText = '';
  loaded = false;
  orgSize: string = '';
  cbItemPriceId: string = '';
  planUid: string = '';
  cardNumberComplete = false;
  cardExpiryComplete = false;
  cardCvcComplete = false;
  zipErrors = [
    {name: 'invalidZip', msg: $localize`The ZIP you have entered is invalid, please try again.`, enabled: false},
  ];
  prices: any;

  planDescriptionOption = {
    '1': $localize`Ideal for organisations with up to 75 employees`,
    '2': $localize`Ideal for organisations with up to 150 employees`,
    '3': $localize`Ideal for organisations with up to 300 employees`,
    '4': $localize`Ideal for organisations with up to 500 employees`
  }

  get SUBTEXT() {
    this.updateCurrentPricing();
    return {
      'monthly': `$${this.currentPricing.monthly.toFixed(2)} per month, for 12 months`,
      'yearly': `$${this.currentPricing.yearly.toFixed(2)} per month, for 12 months`,
    };
  }

  get discountPercentage() {
    const discount = (this.currentPricing.monthly - this.currentPricing.yearly) / this.currentPricing.monthly * 100;
    const roundedDiscount = Math.round(discount * 10) / 10;
    return roundedDiscount.toFixed(1);
}


  get todaysTotal(): string {
    return '0.00';
  }

  get totalAfterTrial(): string {
    if (this.yearlyChecked) {
      return (this.currentPricing.yearly * 12).toFixed(2);
    } else {
      return this.currentPricing.monthly.toFixed(2);
    }
  }

  constructor(
    private modalService: BsModalService,
    public organisationService: OrganisationService,
    private stripeService: StripeService,
    private alertList: AlertBannerService,
    private profile: ProfileService,
    private router: Router,
    private eRef: ElementRef,
    public permissions: PermissionsService,
    private el: ElementRef,
    private renderer: Renderer2,
  ) {
  }

  ngOnInit(): void {
    const currency = Cookies.get('currency');
    const plan = Cookies.get('plan');
    let orgSize = Cookies.get('org_size');
    if (orgSize) orgSize = decodeURIComponent(orgSize);

    // Initialize basic values
    this.selectedCurrency = currency || 'USD';
    this.responsiveScreen = document.documentElement.clientWidth;
    this.isLoading = true;
    const defaultCountry = COUNTRY_MAPPING.find(c => c.entity === environment.STRIPE.DEFAULT_REGION);
    if (defaultCountry) {
      this.selectedCountry = defaultCountry;
    }
    // Initialize Stripe with default region
    const apiKey = this.getStripeApiKey(environment.STRIPE.DEFAULT_REGION);
    this.stripeService.initStripeKey(apiKey);
    this.stripeService.initStripeElements();
    this.stripeService.updateBillingTimezone(environment.STRIPE.DEFAULT_REGION);
    this.initializeStripeElements()

    // Setup event listeners
    this.setupEventListeners();

    // Initialize dropdown options
    this.initializeDropdownOptions();

    // Load plans and initialize Stripe
    this.loadPlans(plan, orgSize);
    this.updateLanguage();
    document.documentElement.classList.add('min-width-unset');
    document.body.classList.add('min-width-unset');

    // Temporary style adjustment to match Figma design - will be removed when Component Library V2.0 is released
    setTimeout(() => {
        const countryDropdown = document.querySelector('.custom-text-field .select-dropdown');
        if (countryDropdown) countryDropdown.setAttribute('data-dropdown-type', 'country');

        const dropdownButtons = document.querySelectorAll('.custom-text-field .select-dropdown');
        dropdownButtons.forEach(button => {
            button.classList.add('placeholder-text');
            button.classList.add('dropdown-border');
        });
    });
  }

  ngAfterViewInit(): void{
    const element = this.el.nativeElement.querySelector('.invite-check-button-padding');
    this.renderer.setStyle(element, 'padding-right', '112px');
  }
  ngOnDestroy() {
    document.documentElement.classList.remove('min-width-unset');
    document.body.classList.remove('min-width-unset');
  }

  updateLanguage() {
    const languageLocale : string = Cookies.get(ConstantsService.XREF_LOCALE)?.toLocaleLowerCase() || '';
    let updatedProfile = {'preferred_language': languageLocale};
    if(languageLocale){
      this.profile.updateProfile(updatedProfile).subscribe({
        next: ((value: any) => {}),
        error: ((error: any) => {console.log(error);})
      });
    }
  }

  setError(name: string, enabled: boolean) {
    this.zipErrors.find(o => o.name === name)!.enabled = enabled;
  }

  setupEventListeners(): void {
    window.addEventListener('resize', () => {
      this.responsiveScreen = document.documentElement.clientWidth;
    });

    document.addEventListener('click', (event: MouseEvent) => {
      const dropdownContainer = document.querySelector('.currency-dropdown-container');
      if (dropdownContainer && !dropdownContainer.contains(event.target as Node)) {
        this.showCurrencyDropdown = false;
      }
    });
  }

  initializeDropdownOptions(): void {
    this.dropDownOptions = COUNTRY_MAPPING.map(country => ({
      label: country.stripeCode,
      value: country.name
    }));

    // Keep the first five unchanged and sort the rest
    this.dropDownOptions = [
      ...this.dropDownOptions.slice(0, 5), // First five remain unchanged
      ...this.dropDownOptions.slice(5).sort((a, b) => a.value.localeCompare(b.value))
    ];
  }

  loadPlans(plan: string | undefined, orgSize: string | undefined): void {
    if (!orgSize || !ORG_SIZE_MAPPING[orgSize]) {
      this.organisationService.getOrganisation().subscribe({
        next: (res) => {
          orgSize = ORG_SIZE_MAPPING[res?.org_size];
          this.loadPlansWithOrgSize(plan, orgSize);
        },
        error: () => {
          console.warn('Organisation size could not be determined');
          this.loadPlansWithOrgSize(plan, undefined);
        }
      });
    } else {
      this.loadPlansWithOrgSize(plan, ORG_SIZE_MAPPING[orgSize]);
    }
  }

  loadPlansWithOrgSize(plan: string | undefined, orgSize: string | undefined): void {
    this.organisationService.getCbPlanItems().subscribe({
      next: (plans) => {
        const planOrder = ['SS_Starter', 'SS_Growth', 'SS_Plus', 'SS_Premium'];
        const validPlanIds = Object.values(CHARGEBEE_PLANS);

        this.plans = plans
          .filter(plan => plan.cb_id && validPlanIds.includes(plan.cb_id))
          .sort((a, b) => planOrder.indexOf(a.cb_id) - planOrder.indexOf(b.cb_id))
          .map(plan => {
            plan.cb_name = plan.cb_id.replace('SS_', '');
            return plan;
          });

        this.updateCurrentPricing();
        this.setupInitialPlanAndSize(plan, orgSize);
        this.updatePlanTooltipAndDescription(this.orgSize);

        this.isLoading = false;
        this.loaded = true;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        this.isLoading = false;
      }
    });
  }

  private updatePlanTooltipAndDescription(orgSize): void {
    const selectedPlan = this.plans.find(p => p.cb_name === this.preSelectedPlan);
    const turnoverCap = selectedPlan?.cb_meta?.profile_cap || 0;
    this.planTooltipText = this.getPlanToolipText(turnoverCap)
    this.planDescription = this.planDescriptionOption[orgSize];
  }

  private getPlanToolipText(turnoverCap: string){
    return $localize`This plan includes a turnover cap of ${turnoverCap}, which is used for the first Reference or Exit request sent to a profile. Turnover cap extensions can be purchased anytime.`
  }

  private setupInitialPlanAndSize(plan: string | undefined, orgSize: string | undefined): void {
    if (plan) {
      this.preSelectedPlan = this.plans.find(p =>
        p.cb_id === CHARGEBEE_PLANS[plan])?.cb_name;
      this.orgSize = MAP_ORG_SIZE[CHARGEBEE_PLANS[plan]];
    } else if (orgSize) {
      this.orgSize = orgSize;
      this.preSelectedPlan = this.plans.find(p =>
        p.cb_id === PLAN_MAPPING[orgSize])?.cb_name;
    }

    this.planUid = this.plans.find(p => p.cb_name === this.preSelectedPlan)?.uid;
    this.prices = this.plans.find(p => p.cb_name === this.preSelectedPlan)?.item_prices;
    this.cbItemPriceId = this.prices?.find(p =>
      p.currency_display === this.selectedCurrency &&
      p.frequency_display === (this.yearlyChecked ? 'yearly' : 'monthly')
    )?.cb_item_price_id;
  }

  toggleCurrencyDropdown(): void {
    this.showCurrencyDropdown = !this.showCurrencyDropdown;
  }

  selectCurrency(currency): void {
    this.selectedCurrency = currency;
    this.showCurrencyDropdown = false;
    this.changeCurrency(currency);
    this.cbItemPriceId = this.prices?.find(p =>
      p.currency_display === this.selectedCurrency &&
      p.frequency_display === (this.yearlyChecked ? 'yearly' : 'monthly')
    )?.cb_item_price_id;
  }

  // Listen for clicks anywhere on the document
  @HostListener('document:click', ['$event'])
  closeDropdown(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.showCurrencyDropdown = false;
    }
  }

  isStartButtonDisabled(): boolean {
    const hasMissingRequiredFields = !this.textValueFirstName || !this.textValueLastName ||
      !this.state || !this.city || !this.address || !this.country;
    const hasStripeErrors = !!this.stripeErrors.cardNumber || !!this.stripeErrors.expiry || !!this.stripeErrors.cvv;
    const hasIncompleteCard = !this.cardNumberComplete || !this.cardExpiryComplete || !this.cardCvcComplete;
    const hasInvalidZip = !!this.zip && this.zipError;
    return hasMissingRequiredFields || hasStripeErrors || hasIncompleteCard || hasInvalidZip;
  }

  async startFreeTrial(): Promise<void> {
    if (this.isStartButtonDisabled()) return;
    this.loaded = false;
    if (this.isProcessing) return;

    this.isProcessing = true;
    this.paymentError = '';
    try {
      const {paymentMethod, error: paymentMethodError} = await this.stripeService.createPaymentMethod(
        this.cardElement,
        {
          billing_details: {
            name: `${this.textValueFirstName} ${this.textValueLastName}`,
            address: {
              line1: this.address,
              city: this.city,
              state: this.state,
              postal_code: this.zip,
              country: this.selectedCountry?.stripeCode
            }
          }
        }
      );

      if (paymentMethodError) {
        this.paymentError = paymentMethodError.message;
        this.handlePaymentError(paymentMethodError, true);
        return;
      }

      const payload = {
        billing_first_name: this.textValueFirstName,
        billing_last_name: this.textValueLastName,
        address: this.address,
        city: this.city,
        state: this.state,
        country: this.selectedCountry?.stripeCode,
        zip: this.zip,
        billing_timezone: this.selectedCountry?.entity,
        payment_method_id: paymentMethod.id,
        plan_uuid: this.planUid,
        frequency: this.yearlyChecked ? 'yearly' : 'monthly',
        currency: this.selectedCurrency,
        item_price_id: this.cbItemPriceId
      };

      // Initial payment intent confirmation
      const response = await this.organisationService.setupStripeBilling(payload).toPromise();

      // Check if 3DS authentication is required
      if (response?.data?.requires_action) {
        // Handle 3DS authentication
        const {setup_intent_client_secret, setup_intent_id, stripe_customer_id} = response.data;

        // Confirm the setup intent with 3DS
        const {setupIntent, error} = await this.stripeService.confirmCardSetup(setup_intent_client_secret, {
          payment_method: paymentMethod.id
        });

        if (error) {
          this.paymentError = error.message;
          this.handlePaymentError(error.message, true);
          return;
        }

        // Create second payload with setup intent details
        const secondPayload = {
          billing_first_name: this.textValueFirstName,
          billing_last_name: this.textValueLastName,
          address: this.address,
          city: this.city,
          state: this.state,
          country: this.selectedCountry?.stripeCode,
          zip: this.zip,
          currency: this.selectedCurrency,
          billing_timezone: this.selectedCountry?.entity,
          setup_intent_id: setup_intent_id,
          stripe_customer_id: stripe_customer_id,
          frequency: this.yearlyChecked ? 'yearly' : 'monthly',
          plan_uuid: this.planUid,
          item_price_id: this.cbItemPriceId
        };

        // Final confirmation after 3DS
        const finalResponse = await this.organisationService.setupStripeBilling(secondPayload).toPromise();

        if (!finalResponse?.data?.success) {
          this.handlePaymentError('Payment verification failed');
          return;
        }

        // Handle successful payment after 3DS
        this.handleSuccessfulSetup();
      } else if (response?.data?.success) {
        // Handle successful payment without 3DS
        this.handleSuccessfulSetup();
      } else {
        if (response?.data?.payment_error) {
          this.handlePaymentError('Payment failed', true);
        } else {
          // Handle payment failure
          this.handlePaymentError('Payment failed');
        }
      }
    } catch (error: any) {
      const isPaymentError = error?.error?.data?.payment_error === true;
      // Handle any unexpected errors
      this.handlePaymentError(error, isPaymentError);
    }
  }

  handleSuccessfulSetup(): void {
    // Clean up cookies
    Cookies.remove('plan', {domain: environment.COOKIE_DOMAIN});
    Cookies.remove('org_size', {domain: environment.COOKIE_DOMAIN});
    Cookies.remove('currency', {domain: environment.COOKIE_DOMAIN});

    this.isProcessing = false;
    this.loaded = true;
    window.location.href = window.location.href.replace('/billing', '/requests');
  }

  handlePaymentError(error: any, isPaymentError=false): void {
    this.paymentError = 'An unexpected error occurred. Please try again.';
    this.loaded = true;
    let message = isPaymentError ? $localize`The payment method provided is invalid, please try another.` : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
    this.alertList.append([{
      type: AlertType.DANGER,
      message: message,
      keepOnRouteChange: true
    }]);
    this.isProcessing = false;
  }

  initializeStripeElements(): void {
    if (this.stripeService.stripeInitError) {
      return;
    }

    try {
      // Create and mount card number element
      this.cardElement = this.stripeService.mountElement(
        'cardNumber',
        '#card-number-element'
      );

      if (this.cardElement) {
        this.cardElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : ''
          if (stripErrorMsg == "Your card number is incomplete."){
            this.stripeErrors.cardNumber = $localize`You’re credit card number should be 16 digits, please try again.`
          }
          else if (stripErrorMsg == "Your card number is invalid."){
            this.stripeErrors.cardNumber = $localize`The credit card number you have entered is invalid, please try again.`
          }
          else{
            this.stripeErrors.cardNumber = stripErrorMsg;
          }
          this.cardNumberComplete = event.complete || false;
        });
      }

      // Create and mount expiry element
      this.expiryElement = this.stripeService.mountElement(
        'cardExpiry',
        '#card-expiry-element'
      );

      if (this.expiryElement) {
        this.expiryElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : ''
          if (stripErrorMsg == "Your card’s expiration year is in the past."){
            this.stripeErrors.expiry = $localize`This expiration date has already passed, please enter a valid expiration date.`
          }
          else if (stripErrorMsg == "Your card’s expiration date is incomplete."){
            this.stripeErrors.expiry = $localize`Please enter a valid expiration date.`
          }
          else{
            this.stripeErrors.expiry = stripErrorMsg;
          }
          this.cardExpiryComplete = event.complete || false;
        });
      }

      // Create and mount CVV element
      this.cvvElement = this.stripeService.mountElement(
        'cardCvc',
        '#card-cvc-element'
      );

      if (this.cvvElement) {
        this.cvvElement.on('change', (event: any) => {
          const stripErrorMsg = event.error ? event.error.message : ''

          if (stripErrorMsg == "Your card’s security code is incomplete."){
            this.stripeErrors.cvv = $localize`Your CVV should be 3 or 4 digits, please try again.`
          }
          else{
            this.stripeErrors.cvv = stripErrorMsg;
          }

          this.cardCvcComplete = event.complete || false;
        });
      }

    } catch (error) {
      console.error('Error initializing Stripe elements:', error);
    }
  }

  dropDownOptionSelected(event: { value: string }) {
    const selectedCountry = COUNTRY_MAPPING.find(country =>
      country.name === event.value
    );

    // Temporary style adjustment to match Figma design - will be removed when Component Library V2.0 is released
    const button = document.querySelector('[data-dropdown-type="country"]');
    if (button) button.classList.remove('placeholder-text');
    if (button) button.classList.remove('dropdown-border');

    if (selectedCountry) {
      this.selectedCountry = selectedCountry;
      this.country = selectedCountry.name;
      this.selectedStripeCode = selectedCountry.stripeCode;
      this.selectedEntity = selectedCountry.entity;
      if (this.stripeService.billingTimezone !== this.selectedEntity) {
        this.stripeService.updateBillingTimezone(this.selectedEntity);
        const apiKey = this.getStripeApiKey(this.selectedEntity);
        this.stripeService.initStripeKey(apiKey);
        this.stripeService.initStripeElements();
        this.initializeStripeElements();
      }


      this.validateZip();
    }
  }

  getStripeApiKey(billingTimezone: string): string {
    const availableKeys = environment.STRIPE.API_KEYS;

    if (billingTimezone && availableKeys[billingTimezone]) {
      return availableKeys[billingTimezone];
    }

    return availableKeys[environment.STRIPE.DEFAULT_REGION];
  }

  updateCurrentPricing(): void {
    const selectedPlan = this.plans.find(plan => plan.cb_name === this.preSelectedPlan);
    if (selectedPlan) {
      const prices = selectedPlan.item_prices.filter(price =>
        price.currency_display === this.selectedCurrency
      );

      const monthlyPrice = prices.find(p => p.frequency_display === 'monthly')?.price || 0;
      const yearlyTotal = prices.find(p => p.frequency_display === 'yearly')?.price || 0;
      const yearlyMonthly = yearlyTotal / 12;

      this.currentPricing = {
        monthly: monthlyPrice,
        yearly: yearlyMonthly  // This is now the monthly price when paying yearly
      };
    }
  }

  monthlyCheck(check: boolean): void {
    if (check) {
      this.monthlyChecked = true;
      this.yearlyChecked = false;
      this.cbItemPriceId = this.prices?.find(p =>
        p.currency_display === this.selectedCurrency &&
        p.frequency_display === (this.yearlyChecked ? 'yearly' : 'monthly')
      )?.cb_item_price_id;
    }
  }

  yearlyCheck(check: boolean): void {
    if (check) {
      this.yearlyChecked = true;
      this.monthlyChecked = false;
      this.cbItemPriceId = this.prices?.find(p =>
        p.currency_display === this.selectedCurrency &&
        p.frequency_display === (this.yearlyChecked ? 'yearly' : 'monthly')
      )?.cb_item_price_id;
    }
  }

  validateZip(): void {
    if (!this.zip || !this.selectedCountry) {
      this.setError('invalidZip', false);
      this.zipError = false;
      return;
    }

    // @ts-ignore
    if (!this.selectedCountry.zipPattern.test(this.zip)) {
      this.setError('invalidZip', true);
      this.zipError = true;
    } else {
      this.setError('invalidZip', false);
      this.zipError = false;
    }
  }

  onZipChange(newValue: string): void {
    this.zip = newValue;
    this.validateZip();
  }

  changeCurrency(currency): void {
    this.selectedCurrency = currency;
    this.updateCurrentPricing();
  }

  openTrialExplainerModal(): void {
    this.trialExplainerModal = this.modalService.show(
      TrialExplainerModalComponent,
      {class: 'modal-dialog-centered trial-explainer'}
    );
  }

  openChangePlanModal(): void {
    const payload = {
      audit_log: 'SIGNUP_CHANGE_PLAN',
      log: {
        current_selected_plan: this.preSelectedPlan,
      }
    }
    this.organisationService.addAuditLog(payload)
    if (this.isLoading || !this.plans || this.plans.length === 0) {
      return;
    }

    const initialState = {
      preSelectedPlan: this.preSelectedPlan,
      billingPeriod: this.yearlyChecked ? 'yearly' : 'monthly',
      selectedCurrency: this.selectedCurrency ? this.selectedCurrency : 'USD',
      plans: this.plans
    };

    this.changePlanModal = this.modalService.show(
      ChangePlanModalComponent,
      {
        class: 'modal-dialog-centered trial-explainer',
        // @ts-ignore
        initialState: initialState
      }
    );

    const component = this.changePlanModal.content as ChangePlanModalComponent;
    component.planSelected.subscribe((planDetails: any) => {
      this.preSelectedPlan = planDetails.name;
      this.planDescription = planDetails.planDescription;
      this.preSelectedPlanDescription = planDetails.description;
      this.selectedCurrency = planDetails.currency
      this.planUid = planDetails.planUid
      this.cbItemPriceId = planDetails.cbItemPriceId
      this.prices = this.plans.find(p => p.cb_name === this.preSelectedPlan)?.item_prices;
      this.updateCurrentPricing()
      this.planTooltipText = this.getPlanToolipText(planDetails.profileCap)
    });
  }
}
